import React from 'react';
import { useProfile } from 'src/models/profile';
import { useTranslation } from 'react-i18next';
import { useRequestStatusMap } from 'src/pages/RequestPage/RequestStatusChangeForm/RequestStatusChangeForm.utils';
import { RequestAttentionAction } from 'src/pages/RequestPage/RequestActionComponents/RequestAttentionAction';
import { If } from 'src/components/If';
import { DownloadFilesDropdown } from 'src/components/download-files-dropdown/download-files-dropdown';
import { countScanHeld } from 'src/features/media/media-scan';
import { type DownloadableFile, getFilenameFromUrl } from 'src/lib/utils/download-utils';
import { RenderForAdmin } from 'src/components/Middleware';
import { roles } from 'src/api/services/RoleClient';
import { useOpenRequestStatusChangeModal } from 'src/features/requests/request-status-change/use-open-request-status-change-modal';
import { RequestStatusCell } from 'src/features/requests/request-table/request-status-cell';
import { useCompanyId } from 'src/features/companies/use-company-id';
import { useCompanyQueryContextData } from 'src/features/companies/use-company-query-context';
import { useRequestId } from 'src/features/requests/use-request-id';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { useRequestQueryContext } from 'src/features/requests/use-request-query-context';
import { useRequestQueryMutationDecorator } from 'src/features/requests/use-request-query-mutation-decorator';
import { useEditorReadOnlyAccess } from 'src/features/requests/hooks/use-editor-read-only-access';
import { EditorReadOnlyBadge } from 'src/features/requests/editor-read-only-notice';
import {
  decorateRevisions,
  useRequestRevisionsStore,
} from 'src/features/revisions/use-revisions-context-provider';
import { requestClient, requestStatus } from 'src/lib/services/api/request-api';
import { Button, ButtonProps } from 'src/components/ui/button';
import { Megaphone, Square, SquareCheckBig } from 'lucide-react';
import { RequestPageHeading } from 'src/features/requests/request-page-heading';
import { AttachRequestTranscriptFormDialog } from 'src/features/requests/attach-request-transcript-form-dialog';
import { DialogTrigger } from 'src/components/ui/dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authQueries } from 'src/api/queries';
import { useToast } from 'src/components/ui/use-toast';

const RevisionsContentTop: React.FC = () => {
  const { t } = useTranslation('pages/shared');

  const companyId = useCompanyId();
  const company = useCompanyQueryContextData();
  const requestId = useRequestId();
  const { canChangeStatus } = useRequestStatusMap();

  const user = useProfile({
    enabled: false,
  });

  const request = useDecoratedRequestContext();
  const isReadOnly = useEditorReadOnlyAccess();
  const requestQueryContext = useRequestQueryContext();
  const requestQueryActions = useRequestQueryMutationDecorator(requestId);
  const { initialize, selectedRevision } = useRequestRevisionsStore();

  const { openRequestStatusChangeModal } = useOpenRequestStatusChangeModal();

  // Memoized so the download callbacks below stay stable between renders.
  const allRevisionsMedia = React.useMemo(
    () => request?.revisions?.filter((r) => r.media).flatMap((r) => r.media!) ?? [],
    [request?.revisions],
  );

  const getAllDownloadableFiles = React.useCallback((): DownloadableFile[] => {
    const seenUrls = new Set<string>();
    const files: DownloadableFile[] = [];

    // Add revision media files
    allRevisionsMedia.forEach((media) => {
      const url = media.index_url || media.restoration_download_url || (media as any).full_url;
      if (!url || seenUrls.has(url)) return;
      seenUrls.add(url);
      files.push({
        url,
        filename: media.file_name || getFilenameFromUrl(url),
        mimeType: media.mime_type || undefined,
      });
    });

    // Add request media files (deduplicated)
    (request?.media ?? []).forEach((media) => {
      const url = media.index_url || media.restoration_download_url || (media as any).full_url;
      if (!url || seenUrls.has(url)) return;
      seenUrls.add(url);
      files.push({
        url,
        filename: media.file_name || getFilenameFromUrl(url),
        mimeType: media.mime_type || undefined,
      });
    });

    return files;
  }, [allRevisionsMedia, request?.media]);
  const countHeldFiles = React.useCallback(
    () => countScanHeld([...allRevisionsMedia, ...(request?.media ?? [])]),
    [allRevisionsMedia, request?.media],
  );

  const changeStatusButtonProps: ButtonProps = {
    variant: 'secondary',
    onClick: () =>
      openRequestStatusChangeModal({
        requestId,
        companyId,
        onSubmit: async ({ status }) => {
          if (status !== requestStatus.delivered) {
            return;
          }

          // If new status is delivered, means we need update revisions store
          const revisions = await requestQueryContext
            .refetch()
            .then(({ data }) => decorateRevisions(data?.revisions ?? []));

          initialize(revisions);
        },
      }),
  };

  if (user.isAdmin && !request.hasEditor) {
    changeStatusButtonProps['title'] = t('warning.request.change_status.unsigned_editor', {
      ns: 'alerts',
    })!;
    changeStatusButtonProps['disabled'] = true;
  }

  if (!canChangeStatus(request.status)) {
    changeStatusButtonProps['title'] = t('warning.request.change_status.customer_action_required', {
      ns: 'alerts',
    })!;
    changeStatusButtonProps['disabled'] = true;
  }

  // A covering editor can watch this request's deliveries but owns none of its
  // workflow — the API refuses the status change, so don't offer it (#2653).
  if (isReadOnly) {
    changeStatusButtonProps['title'] = t(
      'This request is assigned to another editor — you can watch it, but not change it',
    )!;
    changeStatusButtonProps['disabled'] = true;
  }

  const revisionsBreadcrumb = t('revisions.breadcrumb', {
    replace: {
      request: request?.title ?? t('unset', { ns: 'common' }),
    },
  })!;

  const hasAnyRevisionMedia = request?.revisions.some((r) => !!r.media);

  return (
    <div className={'tw-mb-4 tw-flex tw-flex-wrap tw-gap-4'}>
      <RequestPageHeading
        title={revisionsBreadcrumb || t('Request revision')}
        companyId={companyId}
        companyName={company?.name}
        parentRequestId={request.parent_id ?? request.id}
        parentLabel={(request.parent_id ? t('Project') : t('Request'))!}
        currentCrumb={t('Revisions')}
      >
        {/* Same cell the kanban + list views use, so the re-edit loader trails through
            Priority Queue → Editing → Ready for review on the detail page too (#2383). */}
        <div className={'tw-shrink-0'}>
          <RequestStatusCell request={request} />
        </div>
      </RequestPageHeading>

      <div className={'tw-ms-auto tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2'}>
        <If when={isReadOnly}>
          <EditorReadOnlyBadge />
        </If>

        {request.isCompleted && !isReadOnly && (
          <AttachRequestTranscriptFormDialog requestId={requestId}>
            <DialogTrigger asChild>
              <Button variant={'default'}>{t('Upload transcript')}</Button>
            </DialogTrigger>
          </AttachRequestTranscriptFormDialog>
        )}

        <RenderForAdmin and={[roles.teamLead]}>
          <QaCheckButton />
        </RenderForAdmin>

        <If when={hasAnyRevisionMedia || request.hasMedia}>
          <DownloadFilesDropdown
            getFiles={getAllDownloadableFiles}
            countHeld={countHeldFiles}
            zipFileName={`${request?.title || 'request'}-all-files`}
            variant="outline"
          />
        </If>

        <If when={request.canRequestAttention && !isReadOnly}>
          <RequestAttentionAction
            form={{
              requestId: requestId!,
              initialValues: {
                attention_text: request?.attention_text || '',
              },
              onSubmit: async ({ attention_text }) => {
                requestQueryContext.setQueryData({
                  attention_text,
                  requested_attention_at: new Date().toDateString(),
                });
                requestQueryActions.invalidate();
              },
            }}
            as={Button}
            variant={'outline'}
            className={'tw-flex tw-grow tw-items-center sm:tw-grow-0'}
          >
            <span className={'tw-me-3'}>
              <Megaphone className={'tw-h-5 tw-w-5'} />
            </span>
            {t('actions:request_attention')}
          </RequestAttentionAction>
        </If>

        <Button {...changeStatusButtonProps}>
          {t('change_status', { ns: 'actions' })}
          <i className={'bx bx-video-plus ms-2 font-size-18'} />
        </Button>
      </div>
    </div>
  );
};

export { RevisionsContentTop };

const QaCheckButton = () => {
  const client = useQueryClient();
  const request = useDecoratedRequestContext();
  const { t } = useTranslation();
  const { toast } = useToast();

  const changeQAStatus = useMutation({
    mutationFn: (args: Parameters<typeof requestClient.changeQAStatus>) =>
      requestClient.changeQAStatus(...args),
    onError: (e) => {
      toast({
        description: t('Oops, something went wrong, please try again later.'),
        variant: 'destructive',
      });
      console.error(e);
    },
    onMutate: async () => {
      client.setQueryData([authQueries.request, request?.id], (prev: any) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          qa_checked: !request.qa_checked,
        };
      });
    },
    onSuccess: () => {
      toast({
        description: t('Request QA status updated successfully.'),
      });
      client.invalidateQueries({
        queryKey: [authQueries.request, request?.id],
      });
    },
  });

  return (
    <Button
      className={'tw-flex tw-gap-2'}
      variant={'outline'}
      disabled={changeQAStatus.isLoading}
      onClick={() => changeQAStatus.mutate([request.id, { qa_checked: !request.qa_checked }])}
    >
      {request.qa_checked ? <SquareCheckBig size={20} /> : <Square size={20} />}
      {t('QA')}
    </Button>
  );
};
